import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  TrendingUp,
  Users,
  Bell,
  Check,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { timesheetAPI, notificationAPI } from '../services/api';
import { format } from 'date-fns';

const Dashboard = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState(null); // null = all, 'DRAFT', 'SUBMITTED', 'APPROVED'

  const { data: timesheets, isLoading } = useQuery(
    'my-timesheets',
    () => timesheetAPI.getMyTimesheets(),
    {
      select: (response) => {
        const timesheets = response.data.timesheets;
        // Sort by submission date (most recent first)
        return timesheets?.sort((a, b) => {
          // Use submittedAt if available, otherwise use createdAt
          const dateA = new Date(a.submittedAt || a.createdAt);
          const dateB = new Date(b.submittedAt || b.createdAt);
          return dateB - dateA; // Most recent first
        }) || [];
      },
    }
  );

  const { data: pendingTimesheets, isLoading: pendingLoading } = useQuery(
    'pending-timesheets',
    () => timesheetAPI.getPendingTimesheets(),
    {
      enabled: isAdmin,
      select: (response) => response.data.timesheets,
    }
  );

  // Fetch notifications for employee (approved and rejected timesheets)
  // Optimized: Only fetch on mount and window focus, not constantly polling
  const { data: notifications, isLoading: notificationsLoading, error: notificationsError } = useQuery(
    'employee-notifications',
    () => notificationAPI.getNotifications(),
    {
      enabled: !isAdmin && !isSuperAdmin, // Only for employees
      select: (response) => {
        const allNotifications = response.data?.notifications || [];
        // Filter to show only approved and rejected timesheet notifications
        return allNotifications.filter(
          notification => 
            notification.type === 'TIMESHEET_APPROVED' || 
            notification.type === 'TIMESHEET_REJECTED'
        ).slice(0, 10); // Show latest 10 notifications
      },
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: true, // Only refetch when window regains focus
      refetchOnMount: true, // Refetch on component mount
      refetchOnReconnect: true, // Refetch when network reconnects
      refetchInterval: false, // Disable constant polling
      onError: (error) => {
        console.error('Failed to fetch notifications:', error);
      }
    }
  );

  // Mutation to mark notification as read
  const markAsReadMutation = useMutation(
    (notificationId) => notificationAPI.markNotificationAsRead(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employee-notifications');
      }
    }
  );


  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <FileText className="w-5 h-5 text-gray-500" />;
      case 'SUBMITTED':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'PENDING':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter timesheets based on selected status
  const filteredTimesheets = statusFilter 
    ? timesheets?.filter(t => t.status === statusFilter) || []
    : timesheets || [];

  const stats = [
    {
      name: 'Total Timesheets',
      value: timesheets?.length || 0,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      filter: null,
      clickable: true,
      navigateTo: '/timesheets', // Navigate to timesheets list
    },
    {
      name: 'Draft Timesheets',
      value: timesheets?.filter(t => t.status === 'DRAFT').length || 0,
      icon: FileText,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      filter: 'DRAFT',
      clickable: true,
    },
    {
      name: 'Submitted Timesheets',
      value: timesheets?.filter(t => t.status === 'SUBMITTED').length || 0,
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      filter: 'SUBMITTED',
      clickable: true,
    },
    {
      name: 'Approved Timesheets',
      value: timesheets?.filter(t => t.status === 'APPROVED').length || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      filter: 'APPROVED',
      clickable: true,
    },
  ];

  const handleStatClick = (stat) => {
    // If it has navigateTo, navigate to that route
    if (stat.navigateTo) {
      navigate(stat.navigateTo);
      return;
    }
    
    // Otherwise, filter by status
    if (stat.filter === null) return;
    setStatusFilter(statusFilter === stat.filter ? null : stat.filter);
  };

  const adminStats = [
    {
      name: 'Pending Approvals',
      value: pendingTimesheets?.length || 0,
      icon: AlertCircle,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      name: 'Total Employees',
      value: 0, // This would come from a separate API call
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-gray-600 mt-1">
          Here's an overview of your timesheet activity.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => stat.clickable && handleStatClick(stat)}
            className={`bg-white rounded-lg shadow-sm p-6 ${
              stat.clickable 
                ? 'cursor-pointer hover:shadow-md transition-shadow' 
                : ''
            } ${
              statusFilter === stat.filter 
                ? 'ring-2 ring-primary-500 border-2 border-primary-500' 
                : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              {stat.clickable && (
                <div className="text-xs text-gray-400">
                  {stat.navigateTo ? 'Click to view all' : 'Click to filter'}
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Clear Filter Button */}
      {statusFilter && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-end"
        >
          <button
            onClick={() => setStatusFilter(null)}
            className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 font-medium hover:bg-primary-50 rounded-lg transition-colors"
          >
            Clear Filter ({statusFilter})
          </button>
        </motion.div>
      )}

      {/* Timesheet Notifications for Employee */}
      {!isAdmin && !isSuperAdmin && notifications && notifications.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-sm"
        >
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bell className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-medium text-gray-900">Timesheet Notifications</h2>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {notifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.05 }}
                  onClick={() => {
                  // Mark notification as read
                  if (!notification.isRead) {
                    markAsReadMutation.mutate(notification.id);
                  }
                  // Navigate to timesheet
                  if (notification.timesheetId) {
                    navigate(`/timesheets/${notification.timesheetId}`);
                  } else {
                    navigate('/timesheets');
                  }
                }}
                className={`px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  !notification.isRead ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-2 rounded-full ${
                    notification.type === 'TIMESHEET_APPROVED' 
                      ? 'bg-green-100' 
                      : 'bg-red-100'
                  }`}>
                    {notification.type === 'TIMESHEET_APPROVED' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <X className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-medium ${
                        notification.type === 'TIMESHEET_APPROVED' 
                          ? 'text-green-900' 
                          : 'text-red-900'
                      }`}>
                        {notification.title}
                      </p>
                      {!notification.isRead && (
                        <span className="ml-2 h-2 w-2 bg-primary-500 rounded-full"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {format(new Date(notification.createdAt), 'MMM dd, yyyy hh:mm a')}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 text-center">
            <button
              onClick={() => navigate('/timesheets')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View All Timesheets →
            </button>
          </div>
        </motion.div>
      )}

      {/* Admin Stats */}
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {adminStats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (index + 4) * 0.1 }}
              className="bg-white rounded-lg shadow-sm p-6"
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Timesheets List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            {statusFilter 
              ? `${statusFilter} Timesheets` 
              : 'Recent Timesheets'}
          </h2>
          {statusFilter && (
            <span className="text-sm text-gray-500">
              {filteredTimesheets.length} {filteredTimesheets.length === 1 ? 'timesheet' : 'timesheets'}
            </span>
          )}
        </div>
        <div className="divide-y divide-gray-200">
          {filteredTimesheets.length > 0 ? (
            filteredTimesheets.map((timesheet, index) => (
              <motion.div
                key={timesheet.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.05 }}
                onClick={() => navigate(`/timesheets/${timesheet.id}`)}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {getStatusIcon(timesheet.status)}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {format(new Date(timesheet.startDate), 'MMM dd')} - {format(new Date(timesheet.endDate), 'MMM dd, yyyy')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {timesheet.totalHours} hours
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(timesheet.status)}`}>
                      {timesheet.status}
                    </span>
                    <span className="text-sm text-gray-500">
                      {format(new Date(timesheet.createdAt), 'MMM dd, yyyy')}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="px-6 py-8 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {statusFilter 
                  ? `No ${statusFilter.toLowerCase()} timesheets` 
                  : 'No timesheets'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter 
                  ? `You don't have any ${statusFilter.toLowerCase()} timesheets yet.`
                  : 'Get started by creating a new timesheet.'}
              </p>
            </div>
          )}
        </div>
        {!statusFilter && filteredTimesheets.length > 5 && (
          <div className="px-6 py-4 border-t border-gray-200 text-center">
            <button
              onClick={() => navigate('/timesheets')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View All Timesheets →
            </button>
          </div>
        )}
      </motion.div>

      {/* Pending Timesheets for Admin */}
      {isAdmin && pendingTimesheets?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-lg shadow-sm"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Pending Approvals</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {pendingTimesheets.slice(0, 5).map((timesheet, index) => (
              <motion.div
                key={timesheet.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="px-6 py-4 hover:bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {timesheet.employee.firstName} {timesheet.employee.lastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(timesheet.startDate), 'MMM dd')} - {format(new Date(timesheet.endDate), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-500">
                      {timesheet.totalHours} hours
                    </span>
                    <span className="text-sm text-gray-500">
                      {format(new Date(timesheet.submittedAt), 'MMM dd, yyyy')}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};
export default Dashboard;